import { CrudPage } from '../../components/CrudPage';
import { type ResourceTypeDto, ResourceTypeControllerService } from '../../api';
import ResourceTypeFormDialog from '../forms/ResourceTypeFormDialog';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { useTranslation } from 'react-i18next';

// Type with required id for the CrudPage
type ResourceTypeWithId = ResourceTypeDto & { id: string };

const ResourceTypesPage = () => {
  const { t } = useTranslation(['common']);

  // Formatter for category
  const formatCategory = (data: any) => {
    const resourceType = data?.data || data?.rowData || data;
    const category = resourceType?.category;
    if (!category) return '';
    switch (category) {
      case 'OVEN':
        return t('resourceType.category.oven');
      case 'PROCESSING_LINE':
        return t('resourceType.category.processingLine');
      case 'OTHERS':
        return t('resourceType.category.others');
      default:
        return category;
    }
  };

  // Formatter for ovenType
  const formatOvenType = (data: any) => {
    const resourceType = data?.data || data?.rowData || data;
    const ovenType = resourceType?.ovenType;
    if (!ovenType) return '';
    switch (ovenType) {
      case 'STONE_OVEN':
        return t('resourceType.ovenType.stoneOven');
      case 'RACK_OVEN':
        return t('resourceType.ovenType.rackOven');
      case 'CONTINUOUS_OVEN':
        return t('resourceType.ovenType.continuousOven');
      case 'FRYER':
        return t('resourceType.ovenType.fryer');
      default:
        return ovenType;
    }
  };

  // Renderer for numStoves
  const NumStovesRenderer = (data: any) => {
    const resourceType = data?.data || data?.rowData || data;
    const numStoves = resourceType?.numStoves;
    return numStoves != null ? numStoves.toString() : '';
  };

  // Checkbox renderer for singleResource
  const SingleResourceCheckboxTemplate = (data: any) => {
    const resourceType = data?.data || data?.rowData || data;
    const value = resourceType?.singleResource ?? false;
    return (
      <div className="flex justify-center">
        <CheckBoxComponent
          checked={value}
          disabled={true}
          cssClass="e-success"
        />
      </div>
    );
  };

  // Renderer for heating phase
  const HeatingPhaseRenderer = (data: any) => {
    const resourceType = data?.data || data?.rowData || data;
    const seconds = resourceType?.heatingPhaseSecondsPerDegree;
    if (seconds == null) return '';
    return `${seconds} s/°C`;
  };

  // Renderer for cooling phase
  const CoolingPhaseRenderer = (data: any) => {
    const resourceType = data?.data || data?.rowData || data;
    const seconds = resourceType?.coolingPhaseSecondsPerDegree;
    if (seconds == null) return '';
    return `${seconds} s/°C`;
  };


  // Helper function to convert ResourceTypeDto[] to ResourceTypeWithId[]
  const fetchData = async (): Promise<ResourceTypeWithId[]> => {
    const data = await ResourceTypeControllerService.findAll();
    return data.map(item => ({
      ...item,
      id: item.uuid || ''
    }));
  };

  return (
    <CrudPage<ResourceTypeWithId>
      titleKey="headers.resourceTypes"
      gridMeta={[
        { field: 'name', headerKey: 'resourceType.grid.field.name', width: 150 },
        { field: 'description', headerKey: 'resourceType.grid.field.description', width: 200 },
        { field: 'category', headerKey: 'resourceType.grid.field.category', width: 120, render: formatCategory },
        { field: 'singleResource', headerKey: 'resourceType.grid.field.singleResource', width: 120, align: 'Center', render: SingleResourceCheckboxTemplate },
        { field: 'ovenType', headerKey: 'resourceType.grid.field.ovenType', width: 120, render: formatOvenType },
        { field: 'numStoves', headerKey: 'resourceType.grid.field.numStoves', width: 100, align: 'Center', render: NumStovesRenderer },
        { field: 'heatingPhaseSecondsPerDegree', headerKey: 'resourceType.grid.field.heatingPhaseSecondsPerDegree', width: 180, align: 'Center', render: HeatingPhaseRenderer },
        { field: 'coolingPhaseSecondsPerDegree', headerKey: 'resourceType.grid.field.coolingPhaseSecondsPerDegree', width: 180, align: 'Center', render: CoolingPhaseRenderer }
      ]}
      fetchData={fetchData}
      saveData={async (data: ResourceTypeWithId) => {
          // Convert back to ResourceTypeDto format
          const apiData: ResourceTypeDto = {
            ...data,
            uuid: data.id || undefined,
            batchProcessing: false, // Batch processing no longer supported
            synchronicity: false // Synchronicity requires batch processing
          };
          delete (apiData as any).id; // Remove the id field

          if (data.id) {
            const updated = await ResourceTypeControllerService.update(data.id, apiData);
            // Return the updated data to ensure the grid is updated
            return {
              ...updated,
              id: updated.uuid || data.id
            };
          } else {
            const created = await ResourceTypeControllerService.create(apiData);
            // Return the created data to ensure the grid is updated
            return {
              ...created,
              id: created.uuid || ''
            };
          }
      }}
      deleteData={(id: string) => ResourceTypeControllerService.delete(id)}
      FormComponent={ResourceTypeFormDialog}
      entityName="resourcetype"
    />
  );
};

export default ResourceTypesPage;
